import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import * as Api from "@modules/api";
import { Modal } from "@components/items/display/Modal";
import { useInfiniteScroll } from "@utils/hooks/useInfiniteScroll";
import {
  getBookmarked,
  getCompleted,
  getFavorite,
  getHighlighted,
  getStarted,
  useGetBookmarked,
  useGetCompleted,
  useGetFavorite,
  useGetHighlighted,
  useGetStarted,
} from "@lib/stores";
import { Card } from "@ui/card";
import { ProgressBar } from "@components/items/display/ShadcnProgressBar";

import { LibraryItemDetails } from "./LibraryItemDetailsModal";

export type LibraryType =
  | "Started"
  | "Completed"
  | "Favorite"
  | "Bookmarked"
  | "Highlighted";

interface LibraryProgressListSeeMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: LibraryType | undefined;
  onItemClick: (item: LibraryItemDetails) => void;
}

const PAGE_SIZE = 10;

export const LibraryProgressListSeeMoreModal = ({
  isOpen,
  onClose,
  type,
  onItemClick,
}: LibraryProgressListSeeMoreModalProps) => {
  const [pageNumber, setPageNumber] = useState(1);

  const {
    data: startedData,
    meta: startedMeta,
    status: startedStatus,
  } = useGetStarted();

  const {
    data: completedData,
    meta: completedMeta,
    status: completedStatus,
  } = useGetCompleted();

  const {
    data: favoriteData,
    meta: favoriteMeta,
    status: favoriteStatus,
  } = useGetFavorite();

  const {
    data: bookmarkedData,
    meta: bookmarkedMeta,
    status: bookmarkedStatus,
  } = useGetBookmarked();

  const {
    data: highlightedData,
    meta: highlightedMeta,
    status: highlightedStatus,
  } = useGetHighlighted();

  const library = () => {
    switch (type) {
      case "Started":
        return {
          data: startedData,
          meta: startedMeta,
          status: startedStatus,
        };
      case "Completed":
        return {
          data: completedData,
          meta: completedMeta,
          status: completedStatus,
        };
      case "Favorite":
        return {
          data: favoriteData,
          meta: favoriteMeta,
          status: favoriteStatus,
        };
      case "Bookmarked":
        return {
          data: bookmarkedData,
          meta: bookmarkedMeta,
          status: bookmarkedStatus,
        };
      case "Highlighted":
        return {
          data: highlightedData,
          meta: highlightedMeta,
          status: highlightedStatus,
        };
      default:
        return undefined;
    }
  };

  const { lastElementRef } = useInfiniteScroll(
    library()?.meta.hasNextPage,
    setPageNumber
  );

  useEffect(() => {
    switch (type) {
      case "Started":
        getStarted({
          PageNumber: pageNumber,
          PageSize: PAGE_SIZE,
          SortBy: Api.Server.SortBy.MostRecent,
        });

        break;
      case "Completed":
        getCompleted({
          PageNumber: pageNumber,
          PageSize: PAGE_SIZE,
          SortBy: Api.Server.SortBy.MostRecent,
        });

        break;
      case "Favorite":
        getFavorite({
          PageNumber: pageNumber,
          PageSize: PAGE_SIZE,
          SortBy: Api.Server.SortBy.MostRecent,
        });

        break;
      case "Bookmarked":
        getBookmarked({
          PageNumber: pageNumber,
          PageSize: PAGE_SIZE,
          SortBy: Api.Server.SortBy.MostRecent,
        });

        break;
      case "Highlighted":
        getHighlighted({
          PageNumber: pageNumber,
          PageSize: PAGE_SIZE,
          SortBy: Api.Server.SortBy.MostRecent,
        });

        break;
      default:
        break;
    }
  }, [pageNumber, type]);

  const renderLibrary = () => {
    return library()?.data.map(
      (
        {
          id,
          contentEntityTypeId,
          title,
          fullImageUrl,
          authors,
          progress,
          count,
          contentEntityType,
          isLocked,
        },
        i
      ) => {
        const progressModified = Number(((progress || 0) * 100)?.toFixed(0));
        const author = authors?.join(", ");

        return (
          <div
            ref={
              i === (library()?.data.length || 0) - 1 ? lastElementRef : null
            }
            key={id}
          >
            <Card
              className="relative flex w-fit cursor-pointer items-center justify-start gap-8 border-none p-3"
              onClick={() => {
                onItemClick({
                  id,
                  contentEntityTypeId,
                  title,
                  image: fullImageUrl,
                  author,
                  progress: progressModified,
                  count,
                  contentEntityType,
                  isLocked,
                });
              }}
            >
              <div>
                <img
                  src={fullImageUrl || ""}
                  alt="No item"
                  className="h-[150px] w-[161px] rounded-[10px]"
                />
                <div className="flex w-full items-center justify-center gap-3">
                  <ProgressBar value={progressModified} height="h-1" />
                  <div className="text-primary">{progressModified}%</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="w-[200px]">
                  <div className="text-[20px] font-medium text-black dark:text-white">
                    {title}
                  </div>
                  <div className="text-[16px] text-[#898A8D]">{author}</div>
                </div>
                {count ? (
                  <div
                    className={`flex h-[30px] w-[30px] min-w-[30px] items-center justify-center rounded-full text-[20px] font-medium text-white ${
                      type === "Bookmarked" ? "bg-[#FD7777]" : "bg-[#F5DE6A]"
                    }`}
                  >
                    {count}
                  </div>
                ) : null}
              </div>
              {/* {isCompleted && (
        <SVG
          src="/assets/b2c/icons/completed-content.svg"
          className="absolute bottom-5 end-3"
        />
      )} */}
            </Card>
          </div>
        );
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div>
        {renderLibrary()}
        {library()?.status === "loading" ? (
          <div className="flex h-[150px] w-full items-center justify-center p-3 text-black dark:text-white">
            <Loader2 className={`animate-spin`} size={60} strokeWidth={1} />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
